import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import type { DayInsuranceEstimate } from '../../lib/payroll/daily-worker-insurance';
import { formatNumber } from '../../utils/formatters';

interface DailyWorkerInsuranceHintProps {
  estimate: DayInsuranceEstimate | null;
  workDays?: number;
  compact?: boolean;
}

const HintRow: React.FC<{ label: string; amount: number; sub?: string }> = ({
  label,
  amount,
  sub,
}) => (
  <Box
    sx={{
      p: 1.5,
      borderRadius: 1.5,
      bgcolor: 'var(--bg-elev-1)',
      border: '1px solid var(--border-subtle)',
    }}
  >
    <Typography variant="body2" sx={{ color: 'var(--fg-tertiary)', mb: 0.5 }}>
      {label}
    </Typography>
    <Typography
      className="ds-num-md"
      sx={{ color: 'var(--fg-default)', fontWeight: 600 }}
    >
      {formatNumber(amount)}원
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)', display: 'block', mt: 0.5 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const DailyWorkerInsuranceHint: React.FC<DailyWorkerInsuranceHintProps> = ({
  estimate,
  workDays = 1,
  compact = false,
}) => {
  if (!estimate) return null;

  const days = Math.max(1, workDays);
  const employerMonth = estimate.employerEmployment * days;
  const workerMonth = estimate.workerEmployment * days;
  const industrialMonth = estimate.employerIndustrial * days;

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      <Typography variant="body2" sx={{ color: 'var(--fg-secondary)', fontWeight: 600, mb: 1 }}>
        {days > 1 ? `이번 달 (${days}일) 고용·산재 안내` : '1일 기준 고용·산재 안내'}
      </Typography>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={4}>
          <HintRow
            label="고용보험(사업주)"
            amount={days > 1 ? employerMonth : estimate.employerEmployment}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <HintRow
            label="고용보험(근로자)"
            amount={days > 1 ? workerMonth : estimate.workerEmployment}
            sub="※ 실수령에서 공제"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <HintRow
            label="산재보험(사업주)"
            amount={days > 1 ? industrialMonth : estimate.employerIndustrial}
          />
        </Grid>
      </Grid>
      <Typography variant="caption" sx={{ color: 'var(--fg-tertiary)', display: 'block', mt: 1 }}>
        ※ 근로자 고용보험은 실수령에서 공제됩니다. 사업주 부담은 안내용입니다.
      </Typography>
    </Box>
  );
};

export default DailyWorkerInsuranceHint;
